import { useMutation, useQuery } from '@tanstack/react-query';
import { customInstance } from '@/shared/api/mutator/custom-instance';
import { CustomerProfileDTO } from '@/shared/api/model';

interface ProfileAuditLog {
    id: number;
    institutionId: string;
    customerId: string;
    adminUserId: string;
    adminUsername: string;
    actionType: string;
    fieldName: string | null;
    oldValue: string | null;
    newValue: string | null;
    changeReason: string | null;
    ipAddress: string | null;
    createdAt: string;
}

interface CustomerWithProfileStatus {
    branch: string;
    custNo: string;
    name: string;
    phone: string | null;
    mobile: string | null;
    address: string | null;
    dobirth: string | null;
    custSex: string | null;
    idCard: string | null;
    groupCode: string | null;
    groupName: string | null;
    hasProfile: boolean;
    hasNubUser: boolean;
}

// Search profiles API
export const searchProfiles = (searchQuery: string) => {
    return customInstance<CustomerProfileDTO[]>({
        url: '/api/frontend/admin/customer-profile/search',
        method: 'GET',
        params: { searchQuery }
    });
};

export const useSearchProfiles = () => {
    return useMutation({
        mutationFn: (searchQuery: string) => searchProfiles(searchQuery)
    });
};

// Search customers with profile status API
export const searchCustomersWithProfileStatus = (searchQuery: string) => {
    return customInstance<CustomerWithProfileStatus[]>({
        url: '/api/frontend/admin/customer-profile/search-customers',
        method: 'GET',
        params: { searchQuery }
    });
};

export const useSearchCustomersWithProfileStatus = () => {
    return useMutation({
        mutationFn: (searchQuery: string) => searchCustomersWithProfileStatus(searchQuery)
    });
};

// Get profile by customer ID API
export const getProfileById = (customerId: string) => {
    return customInstance<CustomerProfileDTO>({
        url: `/api/frontend/admin/customer-profile/${customerId}`,
        method: 'GET'
    });
};

export const useGetProfileById = (customerId: string, enabled: boolean = true) => {
    return useQuery({
        queryKey: ['admin-profile', customerId],
        queryFn: () => getProfileById(customerId),
        enabled: enabled && !!customerId
    });
};

// Admin update profile API (without OTP)
export const adminUpdateProfile = (customerId: string, data: CustomerProfileDTO) => {
    return customInstance<CustomerProfileDTO>({
        url: `/api/frontend/admin/customer-profile/admin-update/${customerId}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data
    });
};

export const useAdminUpdateProfile = () => {
    return useMutation({
        mutationFn: ({ customerId, data }: { customerId: string; data: CustomerProfileDTO }) =>
            adminUpdateProfile(customerId, data)
    });
};

// Create profile API
export const createProfile = (data: CustomerProfileDTO) => {
    return customInstance<CustomerProfileDTO>({
        url: '/api/frontend/admin/customer-profile',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data
    });
};

export const useCreateProfile = () => {
    return useMutation({
        mutationFn: (data: CustomerProfileDTO) => createProfile(data)
    });
};

// Get audit logs API
export const getAuditLogs = (customerId: string) => {
    return customInstance<ProfileAuditLog[]>({
        url: `/api/frontend/admin/customer-profile/${customerId}/audit-logs`,
        method: 'GET'
    });
};

export const useGetAuditLogs = (customerId: string, enabled: boolean = true) => {
    return useQuery({
        queryKey: ['admin-audit-logs', customerId],
        queryFn: () => getAuditLogs(customerId),
        enabled: enabled && !!customerId
    });
};
